/**
 * Connections — the edges of the graph whose nodes browse lists — as the web
 * app reads them off GET /api/connections: each row's state worked out the
 * way the backend does, and its two ends looked up among the entities the
 * viewer can see.
 */

import dayjs from 'dayjs';

// The states a bond can be in, in the order the table sorts them. The colours
// are antd Tag presets, the same ones ConnectionEditModal uses.
export const CONNECTION_STATES = [
    { value: 'live', label: 'Live', color: 'green' },
    { value: 'pending', label: 'Pending', color: 'orange' },
    { value: 'paused', label: 'Paused', color: 'purple' },
    { value: 'before', label: 'Not started', color: 'blue' },
    { value: 'after', label: 'Ended', color: 'default' },
];

export const stateOf = value => CONNECTION_STATES.find(s => s.value === value) ?? CONNECTION_STATES[0];

const time = iso => new Date(iso).getTime();

/**
 * pending | live | paused | before | after — mirrors services/bonds.py
 * state(), the one definition of a live bond. The connection listing doesn't
 * carry it (only an entity's /connected rows do), so it's worked out here from
 * the same four fields, in the same order: not accepted, then paused, then
 * outside its window.
 */
export function connectionState(conn, at = new Date()) {
    if (!conn.connected_status) return 'pending';
    const t = at.getTime();
    if (conn.paused_until && time(conn.paused_until) > t) return 'paused';
    if (conn.starts_at && time(conn.starts_at) > t) return 'before';
    if (conn.ends_at && time(conn.ends_at) <= t) return 'after';
    return 'live';
}

// A connection's ends are ordered by id (entity_a_id < entity_b_id), which
// says nothing about them — so the kind of an edge names its two types in a
// fixed order instead, and "person – context" is one kind whichever end the
// person happens to be.
const TYPE_RANK = { person: 0, app: 1, context: 2, relationship: 3 };

export function edgeKind(a, b) {
    return [a.entity_type, b.entity_type]
        .sort((x, y) => (TYPE_RANK[x] ?? TYPE_RANK.relationship + 1) - (TYPE_RANK[y] ?? TYPE_RANK.relationship + 1))
        .join(' – ');
}

/**
 * Table rows: each connection with its ends (`a`, `b`), its creator when
 * known, its `state` and its `kind`. A connection whose end isn't among
 * `entityById` is left out — that end is something the viewer can't browse
 * (unlisted, archived, a type this node has off), and so is the edge to it.
 */
export function buildEdgeRows(connections, entityById, at = new Date()) {
    return connections.flatMap(conn => {
        const a = entityById.get(conn.entity_a_id);
        const b = entityById.get(conn.entity_b_id);
        if (!a || !b) return [];
        return [{
            ...conn,
            a,
            b,
            creator: conn.created_by != null ? (entityById.get(conn.created_by) ?? null) : null,
            state: connectionState(conn, at),
            kind: edgeKind(a, b),
        }];
    });
}

// A context or a relationship: the end whose admins accept requests and
// manage memberships, and the side a connection is looked at from.
export const isGroup = entity => entity?.entity_type === 'context' || entity?.entity_type === 'relationship';

/**
 * [viewpoint, other]: the end a connection is managed from — the group's,
 * when exactly one end is a group — and the end it is managed about. What
 * ConnectionEditModal takes as currentEntityId and conn.entity.
 */
export function edgeViewpoint(edge) {
    return isGroup(edge.b) && !isGroup(edge.a) ? [edge.b, edge.a] : [edge.a, edge.b];
}

/**
 * How an edge reads as one line: the person end first, when there is one,
 * since that's how a membership is said ("Ali ↔ Choir"), else A then B.
 */
export function edgeLabel(edge) {
    const personSecond = edge.a.entity_type !== 'person' && edge.b.entity_type === 'person';
    const [first, second] = personSecond ? [edge.b, edge.a] : [edge.a, edge.b];
    return { first, second, name: `${first.name} ↔ ${second.name}` };
}

/**
 * The calendar's items for a set of edges (BrowseCalendar). A term — a bond
 * with a window (todo D12) — lies over the days of its window; a pause (D9)
 * sits on the day it lifts, since a pause is a window closed for a while and
 * the lift is the one day worth marking; an edge with neither is one undated
 * item, so the calendar can count what it isn't drawing. Every item points
 * back at its edge, which is what opening it opens. Dots and tags take the
 * state's colour; "Ended" has no preset dot colour, so it takes the muted
 * border token instead.
 */
export function edgeCalendarItems(edges) {
    return edges.flatMap(edge => {
        const { first, name } = edgeLabel(edge);
        const s = stateOf(edge.state);
        const base = {
            name,
            picture: first,
            edge,
            color: s.color === 'default' ? 'var(--border-color-strong)' : s.color,
            tag: { text: s.label, color: s.color },
        };
        const items = [];
        if (edge.starts_at || edge.ends_at) {
            items.push({ ...base, id: edge.id, starts_at: edge.starts_at, ends_at: edge.ends_at });
        }
        if (edge.state === 'paused' && edge.paused_until) {
            items.push({
                ...base,
                id: `pause-${edge.id}`,
                starts_at: edge.paused_until,
                ends_at: null,
                when: `Paused until ${dayjs(edge.paused_until).format('D MMM YYYY')}`,
            });
        }
        return items.length > 0 ? items : [{ ...base, id: edge.id, starts_at: null, ends_at: null }];
    });
}
